import { useEffect, useState } from 'react';
import { getWelcomeScreenDurationMs, shouldShowFirstVisitSplash } from '../utils/firstVisitSplashUtil';
import { FIRST_VISIT_SPLASH_EXIT_MS } from '../constants/bookingConst';

/**
 * Drives the welcome shown on every page load (guest pages and admin): decides on
 * mount whether this route gets it, then runs a fixed timeline —
 * visible -> leaving -> hidden over REACT_APP_WELCOME_SCREEN_SECONDS
 * (.env.common; default 5s).
 *
 * Deliberately blind to App Check, sign-in and config loading: the welcome
 * never waits for them. They start on their own (firebase.js at import,
 * AuthProvider and ConfigProvider on mount), run behind the welcome, and
 * whatever is still loading when it ends continues in the first screen.
 *
 * @returns {{ visible: boolean, leaving: boolean }}
 *   visible - render the welcome at all; leaving - play its exit animation.
 */
export function useFirstVisitSplash() {
  const [showOnLoad] = useState(() => shouldShowFirstVisitSplash(window.location.pathname));
  const [phase, setPhase] = useState(showOnLoad ? 'visible' : 'hidden');

  useEffect(() => {
    if (!showOnLoad) return undefined;
    const durationMs = getWelcomeScreenDurationMs();
    // The closing wipe runs inside the duration, so the first screen is
    // fully revealed exactly when it ends.
    const leaveTimer = setTimeout(() => setPhase('leaving'), durationMs - FIRST_VISIT_SPLASH_EXIT_MS);
    const hideTimer = setTimeout(() => setPhase('hidden'), durationMs);
    return () => {
      clearTimeout(leaveTimer);
      clearTimeout(hideTimer);
    };
  }, [showOnLoad]);

  return { visible: phase !== 'hidden', leaving: phase === 'leaving' };
}
